// Modern row-based workflow display component
import React, { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlayIcon,
  PauseIcon,
  ClockIcon,
  GitPullRequest,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArchiveIcon,
  RotateCcw,
  Edit,
} from "lucide-react";
import { WorkflowStatus } from "../../types";
import { StatusBadge } from "../Common/StatusBadge";
import { useWorkflowActions } from "../../hooks/useWorkflowActions";
import { usePRStatus } from "../../hooks/usePRMonitoring";
import { usePipelineConfig } from "../../hooks/usePipelineConfig";
import { useAgentExecutions } from "../../hooks/useAgentExecutions";
import { apiService } from "../../services/api";

interface WorkflowRowProps {
  workflow: WorkflowStatus;
  onRefresh?: () => void;
  onEditClick?: (workflow: WorkflowStatus) => void;
  refreshTrigger?: number;
  "data-testid"?: string;
}

export const WorkflowRow: React.FC<WorkflowRowProps> = ({
  workflow,
  onRefresh,
  onEditClick,
  refreshTrigger,
  "data-testid": testId,
}) => {
  const { toggleWorkflow, changeWorkflowPriority, archiveWorkflow, loading } =
    useWorkflowActions(() => {
      onRefresh?.();
      refetchAgentExecutions();
    });
  const navigate = useNavigate();

  // Get PR status for this workflow (SWRM-30)
  const workflowIdNum = parseInt(workflow.id);
  const shouldFetchPRStatus =
    workflow.status === "running" && !isNaN(workflowIdNum);
  const { prStatus } = usePRStatus(shouldFetchPRStatus ? workflowIdNum : null);
  const { config } = usePipelineConfig();

  // Get agent execution data for dynamic pipeline status
  const { agentExecutions, refetch: refetchAgentExecutions } =
    useAgentExecutions(workflow.id);

  // Fetch database pipeline steps if workflow has pipeline_id (SWRM-64)
  const [pipelineSteps, setPipelineSteps] = useState<any[] | null>(null);

  useEffect(() => {
    if (workflow.pipeline_id) {
      apiService
        .getPipelineSteps(workflow.pipeline_id)
        .then((response) => {
          setPipelineSteps(response.steps);
        })
        .catch((error) => {
          console.error(
            `Error fetching pipeline steps for workflow ${workflow.id}:`,
            error
          );
        });
    }
  }, [workflow.pipeline_id, workflow.id]);

  // Trigger agent executions refresh when dashboard refresh is triggered
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      refetchAgentExecutions();
    }
  }, [refreshTrigger, refetchAgentExecutions, workflow.id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
      case "pr_approved_ready_to_merge":
        return "bg-green-500"; // 🟢 Success
      case "failed":
      case "cancelled":
      case "pr_checks_failed":
        return "bg-red-500"; // 🔴 Error/Failed
      case "running":
      case "in_progress":
      case "pending":
      case "waiting_approval":
      case "waiting_for_pr_checks":
      case "waiting_for_pr_approval":
      case "pr_changes_requested":
        return "bg-yellow-500"; // 🟡 In Progress
      case "archived":
        return "bg-gray-400"; // ⚪ Archived
      default:
        return "bg-blue-500"; // 🔵 Unknown
    }
  };

  const formatDuration = (startTime: Date) => {
    const now = new Date();
    const diff = now.getTime() - new Date(startTime).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Create dynamic stages based on pipeline configuration
  const dynamicStages = useMemo(() => {
    const getAgentStatus = (processorName: string, stepId?: string) => {
      if (!agentExecutions || !Array.isArray(agentExecutions)) {
        return "pending";
      }

      const executions = agentExecutions
        .filter((exec) => {
          const processorMatch = exec.processor_type === processorName;
          const stepMatch = !stepId || exec.step_id === stepId;
          return processorMatch && stepMatch && exec.started_at;
        })
        .sort(
          (a, b) =>
            new Date(b.started_at!).getTime() -
            new Date(a.started_at!).getTime()
        );

      const execution = executions[0];
      if (!execution) {
        return "pending";
      }

      switch (execution.status) {
        case "completed":
          return "completed";
        case "in_progress":
          return "in_progress";
        case "failed":
          return "failed";
        default:
          return "pending";
      }
    };

    // SWRM-64: Use database pipeline steps if available
    if (pipelineSteps && pipelineSteps.length > 0) {
      const stages = [
        { name: "Spec", status: "completed" },
      ];

      const sortedSteps = [...pipelineSteps].sort((a, b) => a.order - b.order);
      sortedSteps.forEach((step) => {
        stages.push({
          name: step.name,
          status: getAgentStatus(step.processor_name, step.step_id),
        });
      });

      return stages;
    }

    // Fallback to YAML config
    if (!config?.execution_order || !config?.config?.agents) {
      return [
        { name: "Spec", status: "completed" },
        { name: "Pseudocode", status: getAgentStatus("pseudocode") },
        { name: "Ticket", status: getAgentStatus("ticket") },
        { name: "Workspace", status: getAgentStatus("workspace") },
        { name: "Coding", status: getAgentStatus("coding") },
      ];
    }

    // Build stages from YAML pipeline configuration
    const stages = [
      { name: "Spec", status: "completed" },
    ];

    config.execution_order.forEach((agentId) => {
      const agent = config.config.agents.find((a) => a.id === agentId);
      const displayName =
        agent?.name
          ?.replace(" Agent", "")
          ?.replace(" Generation", "")
          ?.replace(" Breakdown", "")
          ?.replace(" Creation", "") || agentId;

      stages.push({
        name: displayName,
        status: getAgentStatus(agentId),
      });
    });

    return stages;
  }, [pipelineSteps, config, agentExecutions]);

  const pipelineStages = dynamicStages;

  // Calculate progress from pipeline stages
  const calculatedProgress = useMemo(() => {
    if (pipelineStages.length === 0) return 0;

    const completedStages = pipelineStages.filter(
      (stage) => stage.status === "completed"
    ).length;

    return (completedStages / pipelineStages.length) * 100;
  }, [pipelineStages]);

  const handleToggleWorkflow = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const wasRunning = workflow.status === "running";
      await toggleWorkflow(workflow.id, workflow.status);

      if (!wasRunning) {
        navigate(`/workflows/${workflow.id}`);
      }
    } catch (error) {
      console.error("Failed to toggle workflow:", error);
    }
  };

  const handlePriorityChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    e.stopPropagation();
    try {
      await changeWorkflowPriority(
        workflow.id,
        e.target.value as "low" | "medium" | "high"
      );
    } catch (error) {
      console.error("Failed to change priority:", error);
    }
  };

  const handleResetWorkflow = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const message =
      `⚠️ Are you sure you want to RESET workflow "${workflow.name}"?\n\n` +
      "This will DELETE:\n" +
      "• All artifacts and execution data\n" +
      "• All LLM conversations\n" +
      "• All tasks and tickets\n" +
      "• The workspace directory (if created)\n\n" +
      "The workflow will be reset to its initial state.\n\n" +
      "This action CANNOT be undone!";

    if (window.confirm(message)) {
      try {
        const token = localStorage.getItem("auth_token");
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/api/workflows/${workflow.id}/reset`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to reset workflow");
        }

        const data = await response.json();

        const statusMsg = data.status_updated
          ? `\n\nWorkflow status: ${data.new_status}`
          : "\n\n⚠️ Warning: Status update may have failed (check errors)";

        alert(
          `Workflow reset successfully!\n\nDeleted:\n` +
            `• ${data.artifacts_deleted} artifacts\n` +
            `• ${data.executions_deleted} executions\n` +
            `• ${data.conversations_deleted} conversations\n` +
            `• ${data.tasks_deleted} tasks\n` +
            `• ${data.tickets_deleted} tickets\n` +
            `${data.workspace_deleted ? "• Workspace directory\n" : ""}` +
            `\nRegenerated:\n` +
            `• ${data.artifacts_regenerated} input artifacts\n` +
            statusMsg +
            (data.errors && data.errors.length > 0
              ? `\n\n⚠️ Errors:\n${data.errors.join("\n")}`
              : "")
        );

        setTimeout(() => {
          onRefresh?.();
        }, 500);
      } catch (error: any) {
        console.error("Failed to reset workflow:", error);
        alert(`Failed to reset workflow: ${error.message}`);
      }
    }
  };

  const handleArchiveWorkflow = async (e: React.MouseEvent) => {
    e.stopPropagation();

    let message = `Are you sure you want to archive workflow "${workflow.name}"?\n\n`;
    if (workflow.status === "running") {
      message +=
        "⚠️ This workflow is currently running and will be stopped and cancelled before archiving.\n\n";
    }
    message +=
      "This will remove it from the dashboard (but not delete it from the database).";

    if (window.confirm(message)) {
      try {
        await archiveWorkflow(workflow.id);
      } catch (error) {
        console.error("Failed to archive workflow:", error);
      }
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEditClick?.(workflow);
  };

  const handleRowClick = () => {
    navigate(`/workflows/${workflow.id}`);
  };

  // Helper function to get PR status icon and color
  const getPRStatusDisplay = () => {
    if (!prStatus?.pr_status) return null;

    const { ci_status, review_status, ready_to_merge, blocking_issues } =
      prStatus.pr_status;

    if (blocking_issues.length > 0) {
      return {
        icon: <AlertTriangle className="h-3.5 w-3.5" />,
        color: "text-red-600 dark:text-red-400",
        status: "Blocked",
        tooltip: `PR blocked: ${blocking_issues[0]}`,
      };
    }

    if (ready_to_merge) {
      return {
        icon: <CheckCircle className="h-3.5 w-3.5" />,
        color: "text-green-600 dark:text-green-400",
        status: "Ready",
        tooltip: "PR ready to merge",
      };
    }

    if (ci_status === "failure") {
      return {
        icon: <XCircle className="h-3.5 w-3.5" />,
        color: "text-red-600 dark:text-red-400",
        status: "CI Failed",
        tooltip: "CI checks failing",
      };
    }

    if (review_status === "changes_requested") {
      return {
        icon: <XCircle className="h-3.5 w-3.5" />,
        color: "text-orange-600 dark:text-orange-400",
        status: "Changes Req",
        tooltip: "Changes requested in review",
      };
    }

    if (ci_status === "pending" || review_status === "pending") {
      return {
        icon: <ClockIcon className="h-3.5 w-3.5" />,
        color: "text-yellow-600 dark:text-yellow-400",
        status: "Pending",
        tooltip: "Waiting for CI/reviews",
      };
    }

    return {
      icon: <GitPullRequest className="h-3.5 w-3.5" />,
      color: "text-blue-600 dark:text-blue-400",
      status: "Active",
      tooltip: "PR monitoring active",
    };
  };

  const prStatusDisplay = getPRStatusDisplay();

  return (
    <div
      className="group bg-white dark:bg-gray-800 hover:bg-gray-50/60 dark:hover:bg-gray-700/20 border-b border-gray-200 dark:border-gray-700 transition-all duration-200 cursor-pointer"
      data-testid={testId}
      onClick={handleRowClick}
    >
      <div className="flex items-center px-6 py-4 gap-4">
        {/* Status Indicator */}
        <div className="flex-shrink-0">
          <div
            className={`w-2 h-2 rounded-full ${getStatusColor(workflow.status)}`}
            title={workflow.status}
          />
        </div>

        {/* Workflow - 1/3 width */}
        <div className="flex-[1_1_33.33%] min-w-0 pr-4">
            <h3
              className="text-sm font-semibold text-gray-900 dark:text-white truncate"
              data-testid="workflow-name"
            >
              {workflow.name}
            </h3>
            <div className="flex items-center mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              <ClockIcon className="h-3 w-3 mr-1" />
              {formatDuration(workflow.startTime)}
            </div>
          </div>

        {/* Remaining 4 columns - 2/3 width split evenly */}
        <div className="flex-[2_2_66.67%] min-w-0 grid grid-cols-4 gap-4 items-center">
          {/* Priority */}
          <div className="flex items-center gap-2">
            {/* Priority Dropdown */}
            <select
              value={workflow.priority}
              onChange={handlePriorityChange}
              onClick={(e) => e.stopPropagation()}
              className={`appearance-none rounded-full px-3 py-1 text-xs font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all ${
                workflow.priority === "high"
                  ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 focus:ring-red-500"
                  : workflow.priority === "medium"
                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 focus:ring-yellow-500"
                  : "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 focus:ring-green-500"
              }`}
              data-testid="priority-dropdown"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>

            {/* PR Status Badge */}
            {prStatusDisplay && (
              <div
                className={`flex items-center gap-1 px-1.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 ${prStatusDisplay.color}`}
                title={prStatusDisplay.tooltip}
                onClick={(e) => e.stopPropagation()}
              >
                {prStatusDisplay.icon}
              </div>
            )}
          </div>

          {/* Metrics */}
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <div>
              <span>Tasks: {workflow.metrics?.completedTasks || 0}/{workflow.metrics?.totalTasks || 0}</span>
              {workflow.metrics?.failedTasks ? (
                <span className="text-red-600 dark:text-red-400 ml-1">
                  ({workflow.metrics.failedTasks} failed)
                </span>
              ) : null}
            </div>
            <div className="mt-0.5">
              <span>Cost: ${workflow.metrics?.cost?.toFixed(2) || "0.00"}</span>
            </div>
          </div>

          {/* Status & Progress */}
          <div>
            <StatusBadge
              status={workflow.status as any}
              size="sm"
              data-testid="workflow-status"
            />
            <div className="mt-1.5">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    workflow.status === "running"
                      ? "bg-gradient-to-r from-blue-500 to-blue-600 animate-pulse"
                      : "bg-blue-500"
                  }`}
                  style={{ width: `${calculatedProgress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 justify-end">
            {workflow.status !== "completed" && (
              <button
                onClick={handleToggleWorkflow}
                disabled={loading[workflow.id]}
                className={`p-1.5 rounded-md text-sm font-medium transition-all hover:scale-105 ${
                  workflow.status === "running"
                    ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 hover:dark:bg-yellow-900/50"
                    : "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 hover:dark:bg-green-900/50"
                } disabled:opacity-50 disabled:hover:scale-100`}
                data-testid={
                  workflow.status === "running" ? "pause-button" : "resume-button"
                }
                title={workflow.status === "running" ? "Pause" : "Resume"}
              >
                {loading[workflow.id] ? (
                  <div className="animate-spin h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" />
                ) : workflow.status === "running" ? (
                  <PauseIcon className="h-3.5 w-3.5" />
                ) : (
                  <PlayIcon className="h-3.5 w-3.5" />
                )}
              </button>
            )}

            <button
              onClick={handleEditClick}
              disabled={loading[workflow.id]}
              className="p-1.5 rounded-md text-sm font-medium transition-all hover:scale-105 bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 hover:dark:bg-blue-900/50 disabled:opacity-50 disabled:hover:scale-100"
              data-testid="edit-button"
              title="Edit workflow settings"
            >
              <Edit className="h-3.5 w-3.5" />
            </button>

            <button
              onClick={handleResetWorkflow}
              disabled={loading[workflow.id]}
              className="p-1.5 rounded-md text-sm font-medium transition-all hover:scale-105 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700/50 dark:text-gray-300 hover:dark:bg-gray-700 disabled:opacity-50 disabled:hover:scale-100"
              data-testid="reset-button"
              title="Reset workflow"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>

            <button
              onClick={handleArchiveWorkflow}
              disabled={loading[workflow.id]}
              className="p-1.5 rounded-md text-sm font-medium transition-all hover:scale-105 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700/50 dark:text-gray-300 hover:dark:bg-gray-700 disabled:opacity-50 disabled:hover:scale-100"
              data-testid="archive-button"
              title="Archive workflow"
            >
              <ArchiveIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

